import React, { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef, useSyncExternalStore, ReactNode } from 'react'
import taapi from './services/taapi'
import type { Project, Question, Task, TabId, WwInit } from './types'
import {
  setTaskCountListener, setTkInitListener, setTaskstreamKicker, setTaskstreamSender,
  dispatchQuestionProgress, handleTkInitResponse, handleTaskQueuePush,
} from './services/TaskManagerService'
import { UpdateQuestionFn } from './common/common'
// import { addToast, removeToast } from './services/Toasts'

// ── Temp-question registry ──────────────────────────────────────────────
// A `sha → question` map lifted into React state by `AppProvider`. Every
// patch produces a NEW map + NEW question reference, so consumers reading
// via `useTempQuestion(sha)` re-render automatically — same story as
// `useAppProject().currentQuestion`. No custom subscribe / notify plumbing.
//
// `isTempSha` is a pure predicate so it stays at module scope; the
// registry itself lives inside the provider (below).

function newTempId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function isTempSha(sha: string | null | undefined): boolean {
  return typeof sha === 'string' && sha.startsWith('temp_')
}

type TempMap = Map<string, any>
const EMPTY_TEMP_MAP: TempMap = new Map()
const TempContext = createContext<TempMap>(EMPTY_TEMP_MAP)

// React hook — returns the live synth object for `sha` from context. When
// `updateQuestion` (or `buildTempQuestion`) replaces the map entry
// with a new reference, the context re-issues and every consumer of this
// hook re-renders naturally.
export function useTempQuestion(sha: string | null | undefined): any | undefined {
  const map = useContext(TempContext)
  return sha ? map.get(sha) : undefined
}

// ── Navigation context ────────────────────────────────────────────────────────
// Isolated so tab switches only re-render nav consumers, not every mounted tab.

interface NavState {
  activeTab: TabId
  setActiveTab: (tab: TabId) => void
}

const NavContext = createContext<NavState | null>(null)

export function useNav(): NavState {
  const ctx = useContext(NavContext)
  if (!ctx) throw new Error('useNav must be used within AppProvider')
  return ctx
}

// ── App context ───────────────────────────────────────────────────────────────

interface AppState {
  // Projects
  projects: Project[]
  isLoading: boolean
  isProcessing: boolean
  processingMessage: string | null
  tabLoading: boolean
  health: { success: boolean; msg: string }
  acInit: Record<string, any>
  styleNames: string[]
  elementGroups: any[]
  currentStyleName: string
  liteMode: boolean
  taskCount: number
  runningTaskCount: number
  queuedTaskCount: number
  activeTasks: Task[]
  pendingKbPath: string | null
  pendingWwInit: WwInit | null
  streamConnected: boolean
  currentQuestionSetTrigger: number
}

const AppContext = createContext<AppState | null>(null)

// ── App project context ───────────────────────────────────────────────────────
// Holds the two state values that flip during typing - `currentProject`
// and `currentQuestion`. Pulled out of AppContext so the rest of the
// surfaces (sidebar counts, health chip, toasts, templates, etc.) don't
// re-render on every keystroke just because the Provider's value object
// flipped. Consumers that actually display project / question data
// subscribe here; the rest stay on `useApp()`.

export interface AppProjectState {
  currentProject: Project | null
  currentQuestion: Question | null
}

const AppProjectContext = createContext<AppProjectState | null>(null)

export function useAppProject(): AppProjectState {
  const ctx = useContext(AppProjectContext)
  if (!ctx) throw new Error('useAppProject must be used within AppProvider')
  return ctx
}

// Selector hook over the project state. Re-renders the calling component
// only when the selector's *output* changes (compared with `===`), not on
// every `currentProject` / `currentQuestion` identity flip.
//
// Inline selectors are fine - the selector is read via a ref so its
// identity churning between renders doesn't invalidate
// useSyncExternalStore's snapshot cache. `getSnapshot` stays stable.
//
// Backed by `subscribeProject` + the ref-backed getters from AppActions.
// The actions context itself never re-renders so this hook never gets
// re-subscribed unnecessarily.
export function useAppProjectSelect<T>(selector: (s: AppProjectState) => T): T {
  const actions = useContext(AppActionsContext)
  if (!actions) throw new Error('useAppProjectSelect must be used within AppProvider')
  const { subscribeProject, getCurrentProject, getCurrentQuestion } = actions
  // Selector lives behind a ref - updated on every render so the
  // *latest* selector runs, while `getSnapshot`'s own identity stays
  // stable (empty-deps useCallback). That keeps
  // `useSyncExternalStore`'s snapshot cache valid: it compares the
  // current selector output against the previously cached one and
  // re-renders only when they differ.
  const selectorRef = useRef(selector)
  selectorRef.current = selector
  const getSnapshot = useCallback(
    () => selectorRef.current({ currentProject: getCurrentProject(), currentQuestion: getCurrentQuestion() }),
    [getCurrentProject, getCurrentQuestion],
  )
  return useSyncExternalStore(subscribeProject, getSnapshot, getSnapshot)
}

// ── App actions context ───────────────────────────────────────────────────────
// Separate context whose value is the *stable* callbacks / setters from
// AppProvider - everything that's `useCallback([])`d or a React useState
// dispatcher. The value object is `useMemo`'d so the reference never
// changes after first mount, which means components reading actions via
// `useAppActions()` never re-render when `currentProject` / `toasts` /
// any other state in AppProvider flips. Migrate any component that only
// needs setters off `useApp()` and onto this hook to drop it from the
// AppContext broadcast.

interface AppActions {
  setCurrentProject: React.Dispatch<React.SetStateAction<Project | null>>
  setCurrentQuestion: React.Dispatch<React.SetStateAction<Question | null>>
  setPendingWwInit: React.Dispatch<React.SetStateAction<WwInit | null>>
  updateQuestion: UpdateQuestionFn
  updateProject: (patch: Partial<Project>) => void
  getQuestion: (sha: string) => Question | null
  // Latest-value getters backed by refs. Components that only need
  // currentProject / currentStyleName at click time (e.g. the Generate
  // buttons firing off a batch) read via these instead of subscribing
  // to the project / style state - staying out of the per-keystroke
  // re-render cascade.
  getCurrentProject: () => Project | null
  getCurrentQuestion: () => Question | null
  getCurrentStyleName: () => string
  // Look up a project by sha across both the active `currentProject`
  // and the `projects` list. Prefers `currentProject` when its sha
  // matches (it's the fully-hydrated copy with questions); otherwise
  // falls back to the lightweight row from pmInit.
  getProject: (projectsha: string) => Project | null
  // Temp-question registry. `buildTempQuestion` clones a base question
  // into a fresh `temp_*` entry (also returned) and puts it in the map
  // so `updateQuestion(tempSha, patch)` can find it later. `patch` is a
  // normal `Partial<Question>` — top-level fields overlay `base`, and
  // `answerobj` merges (base.answerobj ← patch.answerobj) so callers
  // can pass a full answerobj without having to re-spread.
  // `getTempQuestion` is a ref-backed live getter for async / non-hook
  // contexts; reactive consumers use `useTempQuestion(sha)`.
  buildTempQuestion: (base: any, patch?: Partial<Question>) => any
  getTempQuestion: (sha: string) => any | undefined
  // Subscribe to `currentProject` / `currentQuestion` identity changes.
  // The returned function unsubscribes. Combined with the getters above,
  // this is the foundation for `useAppProjectSelect` - selector-style
  // subscription that only re-renders when the selected slice changes.
  subscribeProject: (cb: () => void) => () => void
  kickTaskstream: () => void
  restartTaskstream: () => void
  // "Refer" a question to a tab: sets it as `currentQuestion`, bumps
  // the `currentQuestionSet` nonce (so downstream consumers can key
  // reset effects off explicit referrals — not off any incidental
  // `updateQuestion` patch), and optionally switches to `tab`. This is
  // the canonical entry point for "open Q in Builder / Advanced Edit /
  // Score & Fix" affordances.
  referQuestionToTab: (question: Question | null, tab?: TabId) => void
  setProjects: (p: Project[]) => void
  setIsLoading: (v: boolean) => void
  setIsProcessing: (v: boolean) => void
  setProcessingMessage: (m: string | null) => void
  setTabLoading: (v: boolean) => void
  setHealth: (h: { success: boolean; msg: string }) => void
  setAcInit: (v: Record<string, any>) => void
  setStyleNames: (v: string[]) => void
  setElementGroups: (v: any[]) => void
  setCurrentStyleName: (v: string) => void
  setLiteMode: (v: boolean) => void
  setTaskCount: (n: number) => void
  setPendingKbPath: (p: string | null) => void

}

const AppActionsContext = createContext<AppActions | null>(null)

export function useAppActions(): AppActions {
  const ctx = useContext(AppActionsContext)
  if (!ctx) throw new Error('useAppActions must be used within AppProvider')
  return ctx
}

// URL <-> tab mapping. The visible URL is `/app/<tab path>` so users can
// bookmark / share / reload landing on the right place.
const TAB_PATHS: Record<TabId, string> = {
  knowledge: 'knowledge',
  projectstab: 'bidmanager',
  response: 'builder',
  bideval: 'scorefix',
  wordcount: 'wordwizard',
  advedit: 'advedit',
  quickchat: 'chat',
  chat: 'oldchat',
  tasks: 'tasks',
  styletab: 'templates',
  settings: 'settings',
}
const PATH_TO_TAB: Record<string, TabId> = Object.fromEntries(
  (Object.entries(TAB_PATHS) as [TabId, string][]).map(([k, v]) => [v, k]),
) as Record<string, TabId>

const DEFAULT_TAB: TabId = 'projectstab'

function tabFromUrl(): TabId {
  if (typeof window === 'undefined') return DEFAULT_TAB
  const m = window.location.pathname.match(/^\/app\/([^/?#]+)/)
  if (m) {
    const tab = PATH_TO_TAB[m[1]]
    if (tab) return tab
  }
  return DEFAULT_TAB
}


export function AppProvider({ children }: { children: ReactNode }) {
  const [activeTab, setActiveTabState] = useState<TabId>(() => tabFromUrl())

  // Remembers the last full URL pathname seen for each tab. When the user
  // clicks the side-nav for a tab they've already visited, we restore the
  // deep path (e.g. /app/bidmanager/my-bid/manage/bidsources) instead of
  // collapsing to /app/bidmanager.
  const lastPathByTabRef = useRef<Partial<Record<TabId, string>>>({})
  const activeTabRef = useRef<TabId>(activeTab)
  useEffect(() => { activeTabRef.current = activeTab }, [activeTab])

  const setActiveTab = useCallback((tab: TabId) => {
    // Capture the outgoing tab's current path before we leave it.
    const outgoing = activeTabRef.current
    if (outgoing) lastPathByTabRef.current[outgoing] = window.location.pathname

    setActiveTabState(tab)
    const targetPath = lastPathByTabRef.current[tab] ?? `/app/${TAB_PATHS[tab]}`
    if (window.location.pathname !== targetPath) {
      history.pushState({ cfpTab: tab }, '', targetPath)
    }
  }, [])

  useEffect(() => {
    // Canonicalise the URL on first render so `/app` and `/app/` redirect to
    // `/app/<currentTab>` without adding a history entry. Skip this when the
    // user isn't inside the app shell (e.g. /login), otherwise we'd push
    // /app/<tab> into the address bar while the auth guard bounces them back
    // to /login - producing a visible URL flicker.
    const initialPath = window.location.pathname
    if (initialPath.startsWith('/app')) {
      const targetPath = initialPath.startsWith(`/app/${TAB_PATHS[activeTab]}`)
        ? initialPath
        : `/app/${TAB_PATHS[activeTab]}`
      if (initialPath !== targetPath) {
        history.replaceState({ cfpTab: activeTab }, '', targetPath)
      } else {
        history.replaceState({ cfpTab: activeTab }, '')
      }
      // Seed the memory so the user's first nav back to this tab keeps the
      // path they originally arrived on.
      lastPathByTabRef.current[activeTab] = targetPath
    }

    const onPopState = (e: PopStateEvent) => {
      // Prefer the URL (works for paths edited directly), fall back to state.
      const fromUrl = tabFromUrl()
      const fromState = e.state?.cfpTab as TabId | undefined
      const tab = fromUrl ?? fromState
      if (tab) setActiveTabState(tab)
    }

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }

    window.addEventListener('popstate', onPopState)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('popstate', onPopState)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const [projects, setProjects] = useState<Project[]>([])
  const projectsRef = useRef<Project[]>(projects)
  useEffect(() => { projectsRef.current = projects }, [projects])
  const [currentProject, setCurrentProject] = useState<Project | null>(null)
  const currentProjectRef = useRef<Project | null>(currentProject)
  const [currentQuestion, setCurrentQuestionState] = useState<Question | null>(null)
  const currentQuestionRef = useRef<Question | null>(currentQuestion)
  // Wrap the setter so every non-null assignment produces a fresh
  // shallow-copy reference. Consumers of `useAppProject().currentQuestion`
  // (and any hook using its identity) then re-render even when a caller
  // passes the same object reference in — matches the "refresh even on
  // same object" semantic the UI expects.
  const setCurrentQuestion = useCallback<React.Dispatch<React.SetStateAction<Question | null>>>((action) => {
    setCurrentQuestionState(prev => {
      const next: Question | null = typeof action === 'function'
        ? (action as (p: Question | null) => Question | null)(prev)
        : action
      return next ? { ...next } : null
    })
  }, [])
  // Subscribers fanned out whenever `currentProject` or `currentQuestion`
  // changes. Drives the `useAppProjectSelect` hook - components register a
  // listener, then read the latest ref value through their selector. The
  // hook re-renders the component only when the selector's *output*
  // changes, which for `s => s.currentProject?.projectsha` means "only
  // when the user switches projects" - typing a question is invisible.
  const projectSubscribersRef = useRef<Set<() => void>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [processingMessage, setProcessingMessage] = useState<string | null>(null)
  const [tabLoading, setTabLoading] = useState(false)
  const [health, setHealth] = useState({ success: true, msg: '' })
  const [acInit, setAcInit] = useState<Record<string, any>>({})
  const [styleNames, setStyleNames] = useState<string[]>([])
  const [elementGroups, setElementGroups] = useState<any[]>([])
  const [currentStyleName, setCurrentStyleName] = useState('')
  const currentStyleNameRef = useRef<string>(currentStyleName)
  useEffect(() => { currentStyleNameRef.current = currentStyleName }, [currentStyleName])
  const [liteMode, setLiteMode] = useState(false)
  const [taskCount, setTaskCount] = useState(0)
  const [runningTaskCount, setRunningTaskCount] = useState(0)
  const [queuedTaskCount, setQueuedTaskCount] = useState(0)
  const [activeTasks, setActiveTasks] = useState<Task[]>([])
  const [pendingKbPath, setPendingKbPath] = useState<string | null>(null)
  const [pendingWwInit, setPendingWwInit] = useState<WwInit | null>(null)
  // Nonce bumped every time a caller refers a question to a tab via
  // `referQuestionToTab`. Downstream tabs (Advanced Edit, Builder,
  // Score & Fix, …) key their reset effects off this counter — so an
  // explicit referral always re-initialises the tab, while background
  // `updateQuestion` patches (trace / score results landing on the
  // live question) never do.
  const [currentQuestionSetTrigger, setCurrentQuestionSetTrigger] = useState(0)

  // Temp-question registry — a React state Map so patches produce a
  // brand-new map + question reference that React sees. Every consumer of
  // `useTempQuestion(sha)` (which reads from `TempContext`)
  // then re-renders exactly like consumers of `useAppProject().currentQuestion`
  // do when the live question changes.
  const [tempQuestions, setTempQuestions] = useState<TempMap>(() => new Map())
  // Ref lets synchronous / async code inside the provider read the
  // latest map without going through React state — needed by
  // `updateQuestion` (fires synchronously) and by `getTempQuestion`
  // (called from async loops in the tabs).
  const tempQuestionsRef = useRef(tempQuestions)
  useEffect(() => { tempQuestionsRef.current = tempQuestions }, [tempQuestions])



  useEffect(() => {
    currentProjectRef.current = currentProject
    projectSubscribersRef.current.forEach(cb => { try { cb() } catch { /* ignore */ } })
  }, [currentProject])
  useEffect(() => {
    currentQuestionRef.current = currentQuestion
    projectSubscribersRef.current.forEach(cb => { try { cb() } catch { /* ignore */ } })
  }, [currentQuestion])

  // Stable subscribe function - registers a listener that fires whenever
  // `currentProject` or `currentQuestion` changes identity. Returns an
  // unsubscribe. Drives `useAppProjectSelect`.
  const subscribeProject = useCallback((cb: () => void) => {
    projectSubscribersRef.current.add(cb)
    return () => { projectSubscribersRef.current.delete(cb) }
  }, [])

  // Taskstream connection state. `streamConnected` drives any UI bits that
  // want to surface a connectivity pip; the refs below let `kickTaskstream`
  // operate synchronously regardless of React batching.
  const [streamConnected, setStreamConnected] = useState(false)
  const streamConnectedRef = useRef(false)
  const streamWsRef = useRef<WebSocket | null>(null)
  const streamReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const streamConnectFnRef = useRef<() => void>(() => { })
  // Sends a control message to the server over the live taskstream WebSocket.
  // Returns false when no open socket is available.
  const streamSendFnRef = useRef<(msg: unknown) => boolean>(() => false)

  useEffect(() => {
    setTaskCountListener((running, waiting) => {
      setRunningTaskCount(running)
      setQueuedTaskCount(waiting)
      setTaskCount(running + waiting)
    })
    const unsub = setTkInitListener((result) => {
      setActiveTasks((result.tasks ?? []).filter(t => t.isrunning || t.iswaiting))
    })
    return () => {
      setTaskCountListener(() => { })
      unsub()
    }
  }, [])

  useEffect(() => {
    let destroyed = false

    // Backoff after a drop before we try to reconnect.
    const RECONNECT_DELAY_MS = 10_000

    // Auth gate: /api2/taskstream is authenticated. On /login we have no
    // cookie yet; bail out and let AppPage's mount-time kickTaskstream()
    // start things post-login.
    const hasAuthCookie = () => /(?:^|;\s*)cfp_access_token=[^;]+/.test(document.cookie)

    // Skip the taskstream entirely while the user is on the login page -
    // there's nothing for it to do there and a 401 just adds noise to
    // the console. AppPage's mount-time kickTaskstream() reopens the
    // connection once the user has signed in and navigated to /app.
    const isOnLoginPage = () => {
      const p = window.location.pathname.toLowerCase()
      return p === '/login' || p.startsWith('/login/')
    }

    // Dispatch one JSON event frame from the taskstream WebSocket.
    const handleEventLine = (raw: string) => {
      try {
        const event = JSON.parse(raw.trim())
        if (event.type === 'health') {
          setHealth({ success: event.success ?? false, msg: event.msg ?? '' })
        } else if (event.type === 'questionprogress') {
          if (event.step === 'answerchunk') {
            // eslint-disable-next-line no-console
            console.debug('[chunk]', performance.now().toFixed(0), event.questionsha?.slice(0, 8), `+${event.text?.length ?? 0}ch`, JSON.stringify(event.text?.slice?.(0, 40) ?? ''))
          } else {
            // eslint-disable-next-line no-console
            console.debug('[questionprogress]', event.step, event.questionsha?.slice(0, 8), event.text ?? event.payload)
          }
          dispatchQuestionProgress(event, getQuestion, updateQuestion)
        } else if (event.type === 'tkinit') {
          // tkinit responses are correlated by `id` inside the service; we
          // just hand the whole frame over.
          handleTkInitResponse(event)
        } else if (event.type === 'taskqueue') {
          // Live running/waiting snapshot from the server (pushed on every
          // task state change). Hand off so the queue tab + sidebar count
          // chips update without waiting for the next tkinit.
          handleTaskQueuePush(event)
        }
      } catch { /* tolerate unparseable frames */ }
    }

    // WebSocket transport. Each frame is one JSON event (no `data:` framing
    // or line buffering). The open socket also carries client→server control
    // messages - see `sendTaskstreamMessage` in TaskManagerService.
    const connect = () => {
      if (destroyed) return

      if (streamReconnectTimerRef.current) {
        clearTimeout(streamReconnectTimerRef.current)
        streamReconnectTimerRef.current = null
      }

      if (isOnLoginPage()) {
        setStreamConnected(false); streamConnectedRef.current = false
        return
      }

      if (!hasAuthCookie()) {
        // eslint-disable-next-line no-console
        console.log('[taskstream] no auth cookie')
        setStreamConnected(false); streamConnectedRef.current = false
        return
      }

      // Tear down any previous socket.
      if (streamWsRef.current) {
        const stale = streamWsRef.current
        streamWsRef.current = null
        try { stale.close() } catch { /* already closing */ }
      }

      let opened = false

      const scheduleReconnect = (reason: string) => {
        if (destroyed) return
        if (streamWsRef.current !== ws) return // superseded by a newer connect
        streamWsRef.current = null
        setStreamConnected(false); streamConnectedRef.current = false
        // eslint-disable-next-line no-console
        console.log('[taskstream] ws disconnected (reason:', reason, `) - reconnecting in ${RECONNECT_DELAY_MS}ms`)
        streamReconnectTimerRef.current = setTimeout(() => {
          streamReconnectTimerRef.current = null
          connect()
        }, RECONNECT_DELAY_MS)
      }

      let ws: WebSocket
      try {
        // eslint-disable-next-line no-console
        console.log('[taskstream] ws connecting…')
        ws = new WebSocket(taapi.taskSocketUrl('/api2/taskstream'))
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('[taskstream] ws construct failed', err)
        scheduleReconnect('ws construct failed')
        return
      }
      streamWsRef.current = ws

      ws.onopen = () => {
        opened = true
        // eslint-disable-next-line no-console
        console.log('[taskstream] ws connected')
        setStreamConnected(true); streamConnectedRef.current = true
      }
      ws.onmessage = (e) => {
        if (typeof e.data === 'string') handleEventLine(e.data)
      }
      ws.onerror = () => {
        // The browser fires error then close; let onclose own the recovery.
        // eslint-disable-next-line no-console
        console.log('[taskstream] ws error')
      }
      ws.onclose = () => {
        if (destroyed) return
        if (streamWsRef.current !== ws) return // a newer connect owns the cycle
        scheduleReconnect(opened ? 'ws closed' : 'ws never opened')
      }
    }

    // Send a control message to the server over the live WebSocket. Returns
    // false when no open socket is available.
    streamSendFnRef.current = (msg: unknown) => {
      const ws = streamWsRef.current
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.send(JSON.stringify(msg)) } catch { /* socket gone */ }
        return true
      }
      return false
    }

    // Expose the connect function via a ref so kickTaskstream below can
    // re-invoke it without breaking the effect's closure boundaries.
    streamConnectFnRef.current = () => { connect() }

    connect()
    return () => {
      destroyed = true
      if (streamReconnectTimerRef.current) {
        clearTimeout(streamReconnectTimerRef.current)
        streamReconnectTimerRef.current = null
      }
      if (streamWsRef.current) {
        try { streamWsRef.current.close() } catch { /* already closing */ }
        streamWsRef.current = null
      }
      streamConnectFnRef.current = () => { }
      streamSendFnRef.current = () => false
    }
  }, [])

  // Wake the taskstream loop on demand - call this right before firing
  // any batch / streaming job so we don't miss the first events.
  //   • If we're already connected, this is a no-op. The live socket
  //     stays up and the upcoming events flow straight in.
  //   • If we're in the post-drop backoff window, cancel the timer and
  //     reconnect now so events don't land on a dead socket.
  //   • Otherwise (no controller, or initial fetch still hanging), call
  //     connect() - it aborts whatever stale controller exists and opens
  //     a fresh socket.
  const kickTaskstream = useCallback(() => {
    if (streamConnectedRef.current) {
      // eslint-disable-next-line no-console
      console.log('[taskstream] kicked but already connected - no-op')
      return
    }
    if (streamReconnectTimerRef.current) {
      // eslint-disable-next-line no-console
      console.log('[taskstream] kicked during backoff - reconnecting now')
      clearTimeout(streamReconnectTimerRef.current)
      streamReconnectTimerRef.current = null
    } else {
      // eslint-disable-next-line no-console
      console.log('[taskstream] kicked while disconnected - forcing reconnect')
    }
    streamConnectFnRef.current()
  }, [])

  // Make `kickTaskstream` callable from plain service modules (e.g.
  // QuestionActions.startGenerateQuestions) that can't read React context.
  // Mirrors the setTaskCountListener / setTkInitListener pattern used by
  // TaskManagerService.
  useEffect(() => {
    setTaskstreamKicker(kickTaskstream)
    return () => { setTaskstreamKicker(() => { }) }
  }, [kickTaskstream])

  // Expose the WebSocket sender to service modules too, so they can push
  // control messages (e.g. cancel) over the live taskstream socket.
  const sendTaskstreamMessage = useCallback((msg: unknown) => streamSendFnRef.current(msg), [])
  useEffect(() => {
    setTaskstreamSender(sendTaskstreamMessage)
    return () => { setTaskstreamSender(() => false) }
  }, [sendTaskstreamMessage])

  // Force a taskstream restart even when currently connected. Aborts the
  // current controller (via connect()'s top-of-function teardown) and
  // opens a fresh socket. Used by the debug toolbar in TaskManagerTab.
  const restartTaskstream = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('[taskstream] restart forced')
    if (streamReconnectTimerRef.current) {
      clearTimeout(streamReconnectTimerRef.current)
      streamReconnectTimerRef.current = null
    }
    streamConnectFnRef.current()
  }, [])

  // Canonical "open Q in <tab>" entry point. Sets the current
  // question, bumps `currentQuestionSet` (so tabs keying reset effects
  // off explicit referrals re-initialise), and optionally switches to
  // the target tab. Callers that need to refer without switching (e.g.
  // hover-preview) can omit `tab`.
  const referQuestionToTab = useCallback((question: Question | null, tab?: TabId) => {
    setCurrentQuestion(question)
    setCurrentQuestionSetTrigger(n => n + 1)
    if (tab) setActiveTab(tab)
  }, [setActiveTab, setCurrentQuestion])

  // `addToast` / `removeToast` are imported from `services/Toasts` - the
  // store lives outside React so any module (services, websocket
  // callbacks, async catch blocks) can call addToast without going
  // through a hook. The functions are still re-exported on the
  // `AppActions` bag for existing call sites.

  // Patches the question identified by `sha` inside currentProject.questions[]
  // and mirrors the patch onto currentQuestion when it matches, so consumers
  // reading from either see the change without per-surface plumbing.
  //
  // `patch` is either a static Partial<Question> or a function that receives
  // the current question and returns the patch (needed for taskstream
  // answerchunks that append to the existing answer text). Skipping undefined
  // keys lets callers pass a sparse patch without nuking unrelated fields.
  // `modified` flips to true unless the resolved patch sets it explicitly
  // (e.g. the post-save reset passes `modified: false`).
  const updateQuestion = useCallback<UpdateQuestionFn>((sha, patch) => {
    console.log('updateQuestion');
    const applyInPlace = (target: any): any => {
      for (const [k, v] of Object.entries(patch)) {
        if (v !== undefined) target[k] = v
      }
      if (patch.modified === undefined) target.modified = true
      return target
    }
    const applyCopy = (q: any): any => applyInPlace({ ...q })

    setCurrentProject(prev => {
      if (!prev?.questions) return prev
      const idx = prev.questions.findIndex((q: any) => q.questionsha === sha)
      if (idx < 0) return prev
      const newQs = [...prev.questions]
      newQs[idx] = applyCopy(newQs[idx])
      return { ...prev, questions: newQs }
    })
    setCurrentQuestion(prev => (prev && prev.questionsha === sha ? applyCopy(prev) : prev))
    if (isTempSha(sha)) {
      const cur = tempQuestionsRef.current.get(sha)
      if (cur) {
        const next = applyCopy(cur)
        const nextMap = new Map(tempQuestionsRef.current)
        nextMap.set(sha, next)
        tempQuestionsRef.current = nextMap
        setTempQuestions(nextMap)
      }
      return
    }
  }, [])

  // Look up a question by sha. Backed by a ref so the returned function
  // identity stays stable across renders (the SSE consume effect
  // captures it once at mount with `[]` deps; a useCallback that
  // depends on `currentProject` would have left that handler reading a
  // null project forever).


  const getQuestion = useCallback((sha: string): Question | null => {
    //if temp look in there
    if (isTempSha(sha)) return tempQuestionsRef.current.get(sha) ?? null
    //else look in project
    const qs = (currentProjectRef.current as any)?.questions
    if (!qs) return null
    return qs.find((q: any) => q.questionsha === sha) ?? null
  }, [])

  // Ref-backed live getter for async / non-hook consumers (e.g. an
  // async loop inside `runTweak` that needs the latest synth after
  // the previous await tick).
  const getTempQuestion = useCallback((sha: string): any | undefined => {
    return tempQuestionsRef.current.get(sha)
  }, [])

  // Clone `base` into a fresh `temp_*` question and register it in the
  // React-state map (via `setTempQuestions`). `patch` is a normal
  // `Partial<Question>` — top-level keys overlay `base`, and
  // `answerobj` is merged so a full answerobj payload can ride in
  // without the caller having to re-spread. Fresh `temp_*` shas are
  // stamped on `questionsha` and `answerobj.answersha` last so nothing
  // in the patch can collide with them. The returned reference is the
  // same one held in the map — subsequent `updateQuestion(tempSha,
  // patch)` calls swap the entry with a new reference and React
  // re-renders every `useTempQuestion` consumer.
  const buildTempQuestion = useCallback((base: any, patch?: Partial<Question>): any => {
    if (!base) return null
    const questionSha = `temp_${newTempId()}`
    const answerSha = `temp_${newTempId()}`

    const applyInPlace = (target: any): any => {
      if (!patch) return target
      for (const [k, v] of Object.entries(patch)) {
        if (v !== undefined) target[k] = v
      }
      if ((patch as any).modified === undefined) target.modified = true
      return target
    }
    const q: any = applyInPlace({ ...base })
    q.questionsha = questionSha
    q.answerobj = { ...(q.answerobj ?? {}), answersha: answerSha }
    const nextMap = new Map(tempQuestionsRef.current)
    nextMap.set(questionSha, q)
    tempQuestionsRef.current = nextMap
    setTempQuestions(nextMap)
    return q
  }, [])

  const getProject = useCallback((projectsha: string): Project | null => {
    const cur = currentProjectRef.current
    if (cur && (cur as any).projectsha === projectsha) return cur
    return projectsRef.current.find(p => (p as any).projectsha === projectsha) ?? null
  }, [])

  // Stable ref-backed getters for components that only need the latest
  // value at click time (e.g. Generate buttons firing a batch). Reading
  // these doesn't subscribe the caller to project / style changes, so
  // they stay out of the per-keystroke re-render cascade.
  const getCurrentProject = useCallback((): Project | null => currentProjectRef.current, [])
  const getCurrentQuestion = useCallback((): Question | null => currentQuestionRef.current, [])
  const getCurrentStyleName = useCallback((): string => currentStyleNameRef.current, [])


  // Same shape as updateQuestion but for project-level edits. Flips
  // `modified: true` unless the patch sets it explicitly. Used for rename,
  // clear-all-answers, etc. - anything that should light up the Save Bid
  // button in the BidProjectView toolbar.
  const updateProject = useCallback((patch: Partial<Project>) => {
    setCurrentProject(prev => {
      if (!prev) return prev
      const next: any = { ...prev }
      for (const [k, v] of Object.entries(patch)) {
        if (v !== undefined) next[k] = v
      }
      if (patch.modified === undefined) next.modified = true
      return next
    })
  }, [])

  // Stable-actions bag. Every entry below is either a React useState
  // dispatcher (stable by construction) or a `useCallback([])`d function
  // - so the memo deps are empty and the value object reference never
  // changes after first mount. Components that only need setters can
  // read via `useAppActions()` and stop re-rendering on every keystroke.
  const actions = useMemo<AppActions>(() => ({
    setProjects,
    setCurrentProject, setCurrentQuestion, setPendingWwInit,
    updateQuestion, updateProject, getQuestion,
    getCurrentProject, getCurrentQuestion, getCurrentStyleName,
    getProject,
    buildTempQuestion, getTempQuestion,
    subscribeProject,
    kickTaskstream, restartTaskstream,
    referQuestionToTab,
    setIsLoading,
    setIsProcessing: () => { },
    setProcessingMessage,
    setTabLoading,
    setHealth,
    setAcInit,
    setStyleNames,
    setElementGroups,
    setCurrentStyleName,
    setLiteMode,
    setTaskCount,
    setPendingKbPath,
  }), [])

  // Memoised project slice - re-issues only when `currentProject` or
  // `currentQuestion` actually changes identity. Keeping it separate
  // from AppContext means typing into a question doesn't fan out a
  // re-render to the sidebar / health chip / templates / etc.
  const projectValue = useMemo<AppProjectState>(() => ({
    currentProject, currentQuestion,
  }), [currentProject, currentQuestion])

  // Memoise the nav slice - the value object reference only flips when
  // `activeTab` changes (the setter is stable). Without this every
  // AppProvider render flips the NavContext identity, forcing every
  // `useNav()` consumer to re-render even when the active tab is
  // unchanged.
  const navValue = useMemo(() => ({ activeTab, setActiveTab }), [activeTab, setActiveTab])

  // Memoise the AppContext value too - same reasoning. Without this,
  // every AppProvider render flips the context identity and forces every
  // `useApp()` consumer to re-render regardless of which slice actually
  // changed. The deps list is every individual state value the
  // provider exposes; the value reference only flips when one of them
  // genuinely differs.
  const appValue = useMemo(() => ({
    projects,
    isLoading,
    isProcessing: taskCount > 0,
    processingMessage,
    tabLoading,
    health,
    acInit,
    styleNames,
    elementGroups,
    currentStyleName,
    liteMode,
    taskCount, runningTaskCount, queuedTaskCount,
    activeTasks,
    pendingKbPath,
    pendingWwInit,
    streamConnected,
    currentQuestionSetTrigger,
  }), [
    projects,
    isLoading,
    taskCount,
    processingMessage,
    tabLoading,
    health,
    acInit,
    styleNames,
    elementGroups,
    currentStyleName,
    liteMode,
    runningTaskCount,
    queuedTaskCount,
    activeTasks,
    pendingKbPath,
    pendingWwInit,
    streamConnected,
    currentQuestionSetTrigger,
  ])

  return (
    <NavContext.Provider value={navValue}>
      <AppContext.Provider value={appValue}>
        <AppProjectContext.Provider value={projectValue}>
          <TempContext.Provider value={tempQuestions}>
            <AppActionsContext.Provider value={actions}>
              {children}
            </AppActionsContext.Provider>
          </TempContext.Provider>
        </AppProjectContext.Provider>
      </AppContext.Provider>
    </NavContext.Provider>
  )
}

export function useApp(): AppState {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppProvider')
  return ctx
}
